/**
 * Générateur — Attendu 969 — « Savoir calculer l'aire d'un parallélogramme et
 * de figures complexes. » (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Parallélogrammes
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/969.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Les DEUX cas que le libellé énonce sont servis : le parallélogramme
 *  (cartes 1 à 3) et les figures complexes (cartes 4 à 8). Formule de
 *  référence écrite partout de la même façon : aire = base × hauteur
 *  RELATIVE à cette base (`formule_de_reference_du_parallelogramme`).
 *
 *  1. aire_directe        — niveau 1 : hauteur tracée depuis un sommet, trois
 *                           longueurs cotées dont une inutile.
 *  2. hauteur_quelconque  — niveau 2 : la hauteur part d'un point quelconque du
 *                           côté, avec un angle droit à CHACUNE de ses deux
 *                           extrémités — elle mesure un écartement.
 *  3. paire               — le choix du couple base-hauteur, en QCM.
 *  4. complexe_accolees   — niveau 3 : deux pièces accolées, découpage affiché.
 *  5. complexe_retrait    — niveau 4 : figure obtenue par RETRAIT.
 *  6. complexe_trois      — niveau 5 : trois pièces, découpage affiché.
 *  7. complexe_deduire    — niveau 6 : une dimension à déduire en une étape.
 *  8. decomposer          — niveau 7 : découpage masqué, l'élève choisit la
 *                           décomposition.
 *  9. demi_disques        — niveau 5 : parallélogramme et demi-disques, calcul
 *                           détaillé, π laissé dans le résultat.
 * 10. formule             — transverse : d'où vient base × hauteur.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - base du parallélogramme : entier de 3 à 10 cm ; hauteur : entier de 3 à
 *     9 cm, tirée indépendamment ;
 *   - inclinaison : l'angle aigu du parallélogramme s'écarte de 25 à 65 degrés
 *     de l'angle droit, donc vaut entre 25 et 65 degrés ;
 *   - second côté coté comme les autres (entier), indiscernable des données
 *     utiles (exclusion 7) ;
 *   - pied de la hauteur toujours sur le côté opposé (exclusion 6) ;
 *   - figure complexe : 2 à 4 pièces prises dans la liste fermée, composées par
 *     addition ou par retrait, décomposition unique à l'ordre près, 1 à 3
 *     lignes de découpage, 0 à 2 dimensions à déduire en une seule étape ;
 *   - dimensions cotées : décimales à une décimale de 2 à 12 cm ;
 *   - aucune pièce ne pèse moins d'un dixième de l'aire totale (exclusion 4).
 *  Exclusions : aucune pièce hors de la liste fermée ; aucune décomposition
 *   ambiguë ; aucune dimension à déduire en plus d'une étape ; aucune
 *   conversion d'unité ; aucune hauteur hors du côté opposé.
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. multiplier les deux côtés comme pour un rectangle
 *      → `produit_des_deux_cotes` (paire)
 *   2. ne pas reconnaître comme hauteur un segment qui ne part pas d'un sommet
 *      → `hauteur_hors_sommet` (`erreursVisees` de hauteur_quelconque)
 *   3. prendre pour hauteur le second côté
 *      → `second_cote_pour_hauteur` (paire, aire_directe)
 *   4. additionner là où il faut soustraire
 *      → `addition_au_lieu_de_soustraction` (`erreursVisees` de
 *        complexe_retrait ; distracteur de decomposer)
 *   5. décomposer en pièces dont on ne connaît pas l'aire
 *      → `piece_sans_formule` (decomposer)
 *   6. oublier une pièce, en particulier la plus petite
 *      → `piece_oubliee` (decomposer ; chaque pièce pèse au moins un dixième
 *        de l'aire, donc l'oubli se voit)
 *   7. utiliser une dimension du contour là où il faut celle d'une pièce
 *      → `dimension_du_contour` (`erreursVisees` de complexe_deduire)
 *   8. croire que le périmètre obtenu est la somme des périmètres des pièces
 *      → `perimetres_additionnes` (decomposer)
 *   9. rendre l'aire sans unité ou en centimètres
 *      → `unite_de_longueur` (`erreursVisees` de toutes les saisies, où
 *        l'unité est tapée par l'élève et jamais imprimée)
 *
 *  Graduation → cartes : 1 aire_directe · 2 hauteur_quelconque · 3
 *   complexe_accolees · 4 complexe_retrait · 5 complexe_trois et demi_disques ·
 *   6 complexe_deduire · 7 decomposer · 8 `generate()` sans clé ; transverses :
 *   paire, formule, et le réglage « calcul en une réponse ou détaillé »
 *   (complexe_accolees offre les deux).
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - Le niveau 7 demande que l'élève TRACE une à trois lignes de découpage ;
 *     aucun format de l'application ne valide un tracé sur une figure libre.
 *     Servi en QCM sur la décomposition, découpage masqué.
 *   - Le barème additif du calcul détaillé est obtenu par la phrase à trous,
 *     dont chaque trou est noté séparément.
 *   - La précision attendue sur les figures à disque est annoncée : π laissé
 *     dans le résultat (demi_disques) ou π pris égal à 3,14 et arrondi au
 *     dixième (encoche), l'arrondi n'intervenant qu'à la FIN.
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeTexteTrou, makeOrdonner,
} from '../../../utils';
import {
    paraParVecteurs, dessineQuad, creerFigure, milieu, dist, txtAt, salt, BLEU,
    angleVers, cadreur, segmentPointille, codageDroitInterieur,
} from './svgParallelo';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 969;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_DIRECTE = 'Aire = base × hauteur';
const L_QUELCONQUE = 'La hauteur part d\'où elle veut';
const L_PAIRE = 'La bonne paire base/hauteur';
const L_ACCOLEES = 'Deux pièces accolées';
const L_RETRAIT = 'Une pièce retirée';
const L_TROIS = 'Trois pièces';
const L_DEDUIRE = 'Une dimension à déduire';
const L_DECOMPOSER = 'Décomposer soi-même';
const L_DISQUES = 'Parallélogramme et demi-disques';
const L_FORMULE = 'D\'où vient la formule ?';

const BASE_MIN = 3;
const BASE_MAX = 10;
const HAUT_MIN = 3;
const HAUT_MAX = 9;
const INCLIN_MIN = 25;
const INCLIN_MAX = 65;
const DIM_MIN = 2;
const DIM_MAX = 12;

const VB = { w: 320, h: 230 };
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWXYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);

const E_DEUX_COTES = 'produit_des_deux_cotes';
const E_HORS_SOMMET = 'hauteur_hors_sommet';
const E_SECOND_COTE = 'second_cote_pour_hauteur';
const E_ADDITION = 'addition_au_lieu_de_soustraction';
const E_SANS_FORMULE = 'piece_sans_formule';
const E_OUBLIEE = 'piece_oubliee';
const E_CONTOUR = 'dimension_du_contour';
const E_PERIMETRES = 'perimetres_additionnes';
const E_UNITE = 'unite_de_longueur';

const fr = (x) => String(Math.round(x * 100) / 100).replace('.', ',');

/**
 * Les écritures admises d'une aire : l'unité fait partie de la réponse
 * (`type_reponse` — « saisie numérique avec unité obligatoire »), et elle est
 * comparée comme une unité, pas comme du texte.
 */
const ecrituresAire = (aire) => {
    const n = fr(aire);
    const out = [`${n} cm²`, `${n} cm2`, `${n} cm^2`, `${n} centimètres carrés`, `${n} centimetres carres`];
    if (!Number.isInteger(aire) && Number.isInteger(aire * 2)) {
        const num = Math.round(aire * 2);
        out.push(`${num}/2 cm²`, `${num}/2 cm2`);
    }
    return out;
};

const saisieAire = (aire, texte, label, extra = {}) => makeSaisie(ecrituresAire(aire)[0], texte, {
    ...opts(label),
    strict: true,
    comparaison: 'expression',
    reponses_acceptees: ecrituresAire(aire).slice(1),
    ...extra,
});

const RAPPEL_UNITE = ' <em>Une unité est attendue dans la réponse.</em>';

// ═══════════════════════════════════════════════════════════
// LE PARALLÉLOGRAMME
// ═══════════════════════════════════════════════════════════

/**
 * Base, hauteur relative et second côté, tous entiers et indiscernables les uns
 * des autres (exclusion 7). L'angle aigu s'écarte de 25 à 65 degrés de l'angle
 * droit, et le pied de la hauteur tombe sur le côté opposé (exclusion 6).
 */
const tirerParallelogramme = () => {
    for (let i = 0; i < 400; i++) {
        const b = randInt(BASE_MIN, BASE_MAX);
        const h = randInt(HAUT_MIN, HAUT_MAX);
        const c = randInt(h + 1, BASE_MAX + 2);
        const angle = (Math.asin(h / c) * 180) / Math.PI;
        if (angle < INCLIN_MIN || angle > INCLIN_MAX) continue;
        const decal = Math.sqrt(c * c - h * h);
        if (decal > b) continue;            // le pied reste dans la base
        if (decal < 0.5) continue;          // jamais un rectangle déguisé
        return { b, h, c, angle: Math.round(angle) };
    }
    return { b: 8, h: 4, c: 6, angle: 42 };
};

const figAire = (b, h, c, noms, seed, { depuisSommet = true, t = 0.5 } = {}) => {
    const px = 22;
    const slant = Math.sqrt((c * px) ** 2 - (h * px) ** 2);
    const { A: P0, B: P1, C: P2, D: P3 } = paraParVecteurs({ x: 0, y: 0 }, { x: b * px, y: 0 }, { x: slant, y: -h * px });
    // Départ de la hauteur : le sommet D, ou un point quelconque du côté [DC].
    const depart = depuisSommet ? P3 : { x: P3.x + t * (P2.x - P3.x), y: P3.y };
    const pied = { x: depart.x, y: 0 };
    const { fig, P } = dessineQuad([P0, P1, P2, P3], VB, seed, {
        noms, longueurs: [[0, 1, `${b} cm`, 16], [3, 0, `${c} cm`, 18]], pointilles: [[depart, pied]],
    });
    // Les points cadrés : le cadrage est affine, on y recalcule le segment.
    const k = (P[1].x - P[0].x) / (P1.x - P0.x);
    const dep = { x: P[0].x + (depart.x - P0.x) * k, y: P[0].y + (depart.y - P0.y) * k };
    const pie = { x: dep.x, y: P[0].y };
    const mid = milieu(dep, pie);
    // La hauteur reste DEDANS, contre son segment pointillé — c'est elle qu'elle
    // mesure, pas un côté. On choisit seulement le bord le plus dégagé des deux
    // côtés obliques, au lieu de toujours écrire à gauche (retour prof 2026-09-27).
    const distSg = (Q, A, B) => {
        const vx = B.x - A.x, vy = B.y - A.y;
        const L2 = vx * vx + vy * vy;
        const t = L2 ? Math.max(0, Math.min(1, ((Q.x - A.x) * vx + (Q.y - A.y) * vy) / L2)) : 0;
        return Math.hypot(Q.x - (A.x + t * vx), Q.y - (A.y + t * vy));
    };
    const obliques = [[P[1], P[2]], [P[3], P[0]]];
    const demiH = (`${h} cm`.length * 12 * 0.55) / 2;
    const cote = [-1, 1]
        .map((sens) => ({ sens, x: mid.x + sens * (17 + demiH) }))
        .sort((u, v) => Math.min(...obliques.map(([A, B]) => distSg({ x: v.x, y: mid.y }, A, B)))
            - Math.min(...obliques.map(([A, B]) => distSg({ x: u.x, y: mid.y }, A, B))))[0].sens;
    txtAt(fig, mid.x + cote * 17, mid.y + 4, `${h} cm`, { couleur: BLEU });
    // Codage d'angle droit au pied, et AUSSI au départ quand la hauteur part
    // d'un point quelconque : le double codage dit que le segment mesure
    // l'écartement des deux côtés parallèles (`objets_geometriques` 1).
    codageDroitInterieur(fig, pie, P[1], dep, { c: 9 });
    if (!depuisSommet) {
        codageDroitInterieur(fig, dep, P[2], pie, { c: 9 });
        fig.point(dep, 'H', angleVers(milieu(P[0], P[2]), dep), 13);
    }
    return fig;
};

const genAireDirecte = () => {
    const { b, h, c, angle } = tirerParallelogramme();
    const noms = pickLetters(4);
    const fig = figAire(b, h, c, noms, salt(b, h, c, randInt(0, 999)));
    const texte = `<p>${noms.join('')} est un parallélogramme. Sa base [${noms[0]}${noms[1]}] mesure `
        + `${b} cm, la hauteur relative à cette base mesure ${h} cm, et le côté [${noms[3]}${noms[0]}] `
        + `mesure ${c} cm. Calcule l'aire de ${noms.join('')}.${RAPPEL_UNITE}</p>`;
    return meta(saisieAire(b * h, texte, L_DIRECTE, {
        elements_additionnels: imgEnonce(fig.render()),
    }), 'aire_directe_texte', {
        niveau: 1,
        longueur_de_la_base_du_parallelogramme: b,
        longueur_de_la_hauteur_du_parallelogramme: h,
        second_cote: c,
        inclinaison_du_parallelogramme: angle,
        point_de_depart_de_la_hauteur_tracee: 'un sommet',
        nombre_de_longueurs_cotees: 3,
        aire: b * h,
        erreursVisees: [E_SECOND_COTE, E_UNITE],
    });
};

const genHauteurQuelconque = () => {
    const { b, h, c, angle } = tirerParallelogramme();
    const noms = pickLetters(4);
    const t = randInt(25, 75) / 100;
    const fig = figAire(b, h, c, noms, salt(b, h, c, Math.round(t * 100), randInt(0, 999)), { depuisSommet: false, t });
    const texte = `<p>${noms.join('')} est un parallélogramme. Le segment tracé en pointillés part d'un `
        + `point H du côté [${noms[3]}${noms[2]}], et <b>pas d'un sommet</b> : il porte un angle droit à `
        + `chacune de ses deux extrémités, c'est donc bien une hauteur — il mesure l'écartement des deux `
        + `côtés parallèles. La base [${noms[0]}${noms[1]}] mesure ${b} cm, cette hauteur mesure ${h} cm, `
        + `et le côté [${noms[3]}${noms[0]}] mesure ${c} cm. Calcule l'aire de `
        + `${noms.join('')}.${RAPPEL_UNITE}</p>`;
    return meta(saisieAire(b * h, texte, L_QUELCONQUE, {
        elements_additionnels: imgEnonce(fig.render()),
    }), 'hauteur_quelconque_texte', {
        niveau: 2,
        longueur_de_la_base_du_parallelogramme: b,
        longueur_de_la_hauteur_du_parallelogramme: h,
        second_cote: c,
        inclinaison_du_parallelogramme: angle,
        point_de_depart_de_la_hauteur_tracee: 'un point quelconque du côté',
        nombre_de_longueurs_cotees: 3,
        aire: b * h,
        erreursVisees: [E_HORS_SOMMET, E_UNITE],
    });
};

const genPaire = () => {
    const { b, h, c } = tirerParallelogramme();
    const [nA, nB, nC] = pickLetters(3);
    const options = [
        { choix: `${b * h} cm²`, code: null },
        { choix: `${b * c} cm²`, code: E_DEUX_COTES },
        { choix: `${c * h} cm²`, code: E_SECOND_COTE },
        { choix: `${b * h} cm`, code: E_UNITE },
    ];
    if (new Set(options.map((o) => o.choix)).size !== 4) return genPaire();
    const texte = `<p>Dans un parallélogramme, le côté [${nA}${nB}] mesure ${b} cm, le côté `
        + `[${nB}${nC}] mesure ${c} cm, et la hauteur <b>relative au côté [${nA}${nB}]</b> mesure `
        + `${h} cm. Quelle est son aire ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_PAIRE));
    return meta(payload, 'paire_qcm_unique', {
        niveau: 1,
        longueur_de_la_base_du_parallelogramme: b,
        longueur_de_la_hauteur_du_parallelogramme: h,
        second_cote: c,
        aire: b * h,
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// LES FIGURES COMPLEXES
// ═══════════════════════════════════════════════════════════

const PXC = 22;
const VB_CPLX = { w: 360, h: 280 };
const ENCRE = '#334155';
const FOND = 'rgba(37,99,235,0.10)';
const POINTILLE = '#b45309';
/** Fond de la pièce RETIRÉE : une autre couleur, pour qu'elle se voie comme pièce. */
const FOND_RETRAIT = 'rgba(180,83,9,0.13)';
const ptTxt = (p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`;

const arcPoints = (C, r, a1, a2, n = 24) => {
    const out = [];
    for (let i = 0; i <= n; i++) {
        const a = a1 + ((a2 - a1) * i) / n;
        out.push({ x: C.x + r * Math.cos(a), y: C.y + r * Math.sin(a) });
    }
    return out;
};

const cadrerCplx = (points, pad = 48) => {
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const k = Math.min((VB_CPLX.w - 2 * pad) / (Math.max(...xs) - minX || 1),
        (VB_CPLX.h - 2 * pad) / (Math.max(...ys) - minY || 1));
    const offX = (VB_CPLX.w - k * (Math.max(...xs) - minX)) / 2;
    const offY = (VB_CPLX.h - k * (Math.max(...ys) - minY)) / 2;
    return (p) => ({ x: offX + (p.x - minX) * k, y: offY + (p.y - minY) * k });
};

/**
 * Un libelle de cote tient sur DEUX LIGNES des qu'il porte plus que sa mesure.
 * « hauteur totale 8 cm » fait 125 px sur une ligne : il traversait la figure
 * (retour prof 2026-09-27). Coupe apres « totale », il n'en fait plus que 75.
 */
const couperCote = (txt) => {
    const m = /^(.*\S)\s+([\d,]+\s*cm.*)$/.exec(String(txt));
    return m && m[1].length > 3 ? [m[1], m[2]] : [String(txt)];
};
const demiLargeur = (txt) => (Math.max(...couperCote(txt).map((l) => l.length)) * 12 * 0.55) / 2;
/** Longueur totale du libelle, toutes lignes bout a bout — sa hauteur une fois tourne. */
const demiLongue = (txt) => (String(txt).replace(/\s+/g, ' ').length * 12 * 0.55) / 2;

/**
 * Une cote VERTICALE s'ecrit tournee d'un quart de tour, le long de ce qu'elle
 * mesure. « hauteur totale 11 cm » occupe 92 px de large a plat : dans une figure
 * etroite, il n'y avait aucune place a gauche et le texte traversait le dessin
 * (retour prof 2026-09-27). Tourne, il n'occupe plus que sa hauteur de ligne.
 */
const texteCote = (T, txt, vertical) => {
    if (vertical) {
        return `<text x="${T.x.toFixed(1)}" y="${T.y.toFixed(1)}" font-family="sans-serif" font-size="12" fill="#0f172a" text-anchor="middle" transform="rotate(-90 ${T.x.toFixed(1)} ${T.y.toFixed(1)})">${String(txt).replace(/\s+/g, ' ')}</text>`;
    }
    const li = couperCote(txt);
    if (li.length === 1) {
        return `<text x="${T.x.toFixed(1)}" y="${(T.y + 4).toFixed(1)}" font-family="sans-serif" font-size="12" fill="#0f172a" text-anchor="middle">${txt}</text>`;
    }
    return `<text x="${T.x.toFixed(1)}" y="${(T.y - 2).toFixed(1)}" font-family="sans-serif" font-size="12" fill="#0f172a" text-anchor="middle">`
        + `<tspan x="${T.x.toFixed(1)}">${li[0]}</tspan>`
        + `<tspan x="${T.x.toFixed(1)}" dy="13">${li[1]}</tspan></text>`;
};

const rendreCplx = ({ contour, lignes, cotes, codages, seed, afficherLignes, retrait, hauteurs }) => {
    // La marge doit loger le plus long libelle pousse vers l'exterieur, sinon
    // « hauteur totale 10 cm » sort du cadre.
    const demiMax = cotes.length ? Math.max(...cotes.map((c) => (c.vertical ? 10 : demiLargeur(c.texte)))) : 0;
    // Marge resserree : les libelles tournes ne coutent plus leur largeur, le dessin
    // peut donc occuper davantage la vignette (retour prof 2026-09-27).
    const cad = cadrerCplx([...contour, ...cotes.map((c) => c.p), ...(retrait || [])],
        Math.ceil(Math.min(58, 22 + demiMax)));
    const C = contour.map(cad);
    let g = `<polygon points="${C.map(ptTxt).join(' ')}" fill="${FOND}" stroke="${ENCRE}" stroke-width="2"/>`;
    // LA PIÈCE RETIRÉE, dessinée comme une pièce : fond d'une autre couleur, contour
    // en pointillés. Sans elle, le seul « trait de découpage » de l'encoche était le
    // diamètre du demi-disque — posé exactement sur le bord droit du carré, donc
    // invisible : mesuré à 0,0 px du contour (retour prof 2026-09-27). On voyait
    // l'énoncé parler de pointillés et la figure n'en montrer aucun.
    if (afficherLignes && retrait && retrait.length > 2) {
        g += `<polygon points="${retrait.map(cad).map(ptTxt).join(' ')}" fill="${FOND_RETRAIT}" stroke="${POINTILLE}" stroke-width="1.6" stroke-dasharray="5 4"/>`;
    }
    // Quand la pièce retirée est dessinée, ses arêtes SONT les lignes de découpage :
    // les tracer en plus doublerait les traits.
    if (afficherLignes && !(retrait && retrait.length > 2)) {
        lignes.forEach((l) => {
            const a = cad(l.p1);
            const b = cad(l.p2);
            g += `<line x1="${a.x.toFixed(1)}" y1="${a.y.toFixed(1)}" x2="${b.x.toFixed(1)}" y2="${b.y.toFixed(1)}" stroke="${POINTILLE}" stroke-width="1.6" stroke-dasharray="5 4"/>`;
        });
    }
    // Les hauteurs : pointilles + codage d'angle droit au pied.
    //
    // Elles ne se tracent QUE si le decoupage est montre. Le pied d'une hauteur
    // repose sur la ligne de decoupage ; quand cette ligne est masquee — la carte
    // « decomposer soi-meme » —, la hauteur s'arretait dans le vide, son angle droit
    // accroche a rien (retour prof 2026-09-27, defaut introduit la veille avec le
    // trace des hauteurs).
    (afficherLignes ? hauteurs || [] : []).forEach((hq) => {
        const a2 = cad(hq.p1);
        const b2 = cad(hq.p2);
        g += `<line x1="${a2.x.toFixed(1)}" y1="${a2.y.toFixed(1)}" x2="${b2.x.toFixed(1)}" y2="${b2.y.toFixed(1)}" stroke="${POINTILLE}" stroke-width="1.6" stroke-dasharray="5 4"/>`;
        const c9 = 9;
        const sx = b2.x > a2.x ? 1 : -1;
        const sy = a2.y < b2.y ? -1 : 1;
        g += `<path d="M ${(b2.x + sx * c9).toFixed(1)},${b2.y.toFixed(1)} L ${(b2.x + sx * c9).toFixed(1)},${(b2.y + sy * c9).toFixed(1)} L ${b2.x.toFixed(1)},${(b2.y + sy * c9).toFixed(1)}" fill="none" stroke="${ENCRE}" stroke-width="1.4"/>`;
    });
    // Un codage d'angle droit ne se trace que si son sommet est un sommet DU
    // CONTOUR. Sur la bande, il marque le coin du rectangle interieur : quand le
    // decoupage est masque — carte « decomposer soi-meme » —, ce coin n'existe pas
    // sur le dessin et l'equerre pendait au milieu de la base (retour prof
    // 2026-09-27).
    (codages || []).filter((k) => {
        const V = cad(k.v);
        return C.some((q) => Math.hypot(q.x - V.x, q.y - V.y) < 1.5);
    }).forEach((k) => {
        const V = cad(k.v);
        const u = (T) => {
            const q = cad(T);
            const dx = q.x - V.x;
            const dy = q.y - V.y;
            const L = Math.hypot(dx, dy) || 1;
            return { x: (dx / L) * 10, y: (dy / L) * 10 };
        };
        const a = u(k.p);
        const b = u(k.q);
        g += `<path d="M ${(V.x + a.x).toFixed(1)},${(V.y + a.y).toFixed(1)} L ${(V.x + a.x + b.x).toFixed(1)},${(V.y + a.y + b.y).toFixed(1)} L ${(V.x + b.x).toFixed(1)},${(V.y + b.y).toFixed(1)}" fill="none" stroke="${ENCRE}" stroke-width="1.4"/>`;
    });
    // LES COTES SORTENT DE LA FIGURE. Elles etaient posees a des coordonnees ecrites
    // en dur, famille par famille : « hauteur totale 10 cm » passait sur le dessin, et
    // deux cotes voisines se marchaient dessus (retour prof 2026-09-27). Chacune part
    // de son ancre — donc reste contre le cote qu'elle designe — et recule vers
    // l'exterieur jusqu'a degager le contour ET les cotes deja posees.
    const Gc = C.reduce((acc, q) => ({ x: acc.x + q.x / C.length, y: acc.y + q.y / C.length }), { x: 0, y: 0 });
    const bords = C.map((q, k) => [q, C[(k + 1) % C.length]]);
    const dSeg = (Q, A, B) => {
        const vx = B.x - A.x, vy = B.y - A.y;
        const L2 = vx * vx + vy * vy;
        const t = L2 ? Math.max(0, Math.min(1, ((Q.x - A.x) * vx + (Q.y - A.y) * vy) / L2)) : 0;
        return Math.hypot(Q.x - (A.x + t * vx), Q.y - (A.y + t * vy));
    };
    const poseesC = [];
    // On pose les libelles du plus ENCOMBRANT au plus petit : le large a peu de
    // places possibles, le court en a beaucoup. Dans l'autre sens, le court occupait
    // la seule place du large et c'est le large qui debordait (retour prof).
    const ordre = [...cotes].sort((x, y) => {
        const l = (c) => (c.vertical ? demiLongue(c.texte) : demiLargeur(c.texte));
        return l(y) - l(x);
    });
    ordre.forEach((c) => {
        const p0 = cad(c.p);
        // boite du libelle : large et basse a plat, etroite et haute une fois tourne
        const demi = c.vertical ? 7 : demiLargeur(c.texte);
        // un libelle coupe en deux lignes est DEUX FOIS plus haut : le modeliser sur
        // une seule ligne laissait « longueur totale 9 cm » mordre son voisin.
        const demiV = c.vertical ? demiLongue(c.texte) : (couperCote(c.texte).length > 1 ? 13 : 5.5);
        // Une cote INTERNE — la hauteur — reste dedans, contre son segment : c'est lui
        // qu'elle mesure. Seules les cotes de cote sortent.
        let ux = c.interne ? -1 : p0.x - Gc.x;
        let uy = c.interne ? 0 : p0.y - Gc.y;
        const nu = Math.hypot(ux, uy) || 1;
        ux /= nu; uy /= nu;
        const bordsUtiles = c.interne ? [] : bords;
        // Un libelle est une BOITE : on le sonde sur ses deux bouts ET sur son haut et
        // son bas, sinon deux libelles se chevauchent verticalement.
        const boite = (Q) => {
            const out = [];
            const pasS = Math.max(1, Math.ceil((2 * demiV) / 6));
            for (let k = 0; k <= pasS; k++) {
                const dy = -demiV + (2 * demiV * k) / pasS;
                [-demi, 0, demi].forEach((dx) => out.push({ x: Q.x + dx, y: Q.y + dy }));
            }
            return out;
        };
        // On s'ecarte vers l'exterieur, et si la place est prise on balaie aussi
        // LATERALEMENT : sur la bande, « longueur totale 12 cm » fait 140 px de large
        // et barre tout le bas de la figure ; sans ce balayage, « 5 cm » n'avait
        // nulle part ou aller et se posait dessus.
        let T = null;
        let meilleur = null;
        [0, 5, 10, 16, 22, 29, 36, 44].forEach((d) => {
            [0, -0.45, 0.45, -0.9, 0.9].forEach((biais) => {
                if (T) return;
                const c2 = Math.cos(biais), s2 = Math.sin(biais);
                const vx = ux * c2 - uy * s2, vy = ux * s2 + uy * c2;
                const Q = { x: p0.x + vx * d, y: p0.y + vy * d };
                if (Q.x - demi < 4 || Q.x + demi > VB_CPLX.w - 4 || Q.y - demiV < 8 || Q.y + demiV > VB_CPLX.h - 6) return;
                const libre = Math.min(
                    ...boite(Q).flatMap((R) => (bordsUtiles.length ? bordsUtiles.map(([A, B]) => dSeg(R, A, B)) : [99])),
                    ...boite(Q).flatMap((R) => (poseesC.length ? poseesC.map(([A, B]) => dSeg(R, A, B)) : [99])),
                );
                if (libre >= 8) T = Q;
                if (!meilleur || libre > meilleur.libre) meilleur = { libre, Q };
            });
        });
        // Aucun candidat a 8 px ? On prend LE MOINS MAUVAIS parmi ceux qui tiennent
        // dans le cadre, au lieu d'un decalage fixe pose les yeux fermes.
        if (!T && meilleur) T = meilleur.Q;
        if (!T) T = { x: p0.x + ux * 29, y: p0.y + uy * 29 };
        // dernier garde-fou : rester dans le cadre
        T = {
            x: Math.max(demi + 4, Math.min(VB_CPLX.w - demi - 4, T.x)),
            y: Math.max(demiV + 8, Math.min(VB_CPLX.h - demiV - 6, T.y)),
        };
        // On memorise la boite par des lignes espacees de 6 px au plus : avec trois
        // lignes seulement, un libelle court se glissait ENTRE deux d'entre elles
        // quand la boite est haute — le cas d'un libelle tourne (retour prof).
        const pas = Math.max(1, Math.ceil((2 * demiV) / 6));
        for (let k = 0; k <= pas; k++) {
            const dy = -demiV + (2 * demiV * k) / pas;
            poseesC.push([{ x: T.x - demi, y: T.y + dy }, { x: T.x + demi, y: T.y + dy }]);
        }
        g += texteCote(T, c.texte, c.vertical);
    });
    return `<svg data-v="${seed}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB_CPLX.w} ${VB_CPLX.h}" width="${VB_CPLX.w}" height="${VB_CPLX.h}">${g}</svg>`;
};

const Pc = (x, y) => ({ x: x * PXC, y: y * PXC });

/**
 * Bande : triangle rectangle à gauche (facultatif), rectangle, demi-disque à
 * droite (facultatif). Les traits de découpage sont verticaux et les pièces de
 * natures différentes : aucun autre découpage en pièces de la liste n'est
 * possible, la décomposition est donc unique à l'ordre près (vigilance 2).
 */
const bande = ({ a, w, h, demi }) => {
    const contour = [];
    if (a > 0) contour.push(Pc(0, h));
    contour.push(Pc(a, 0), Pc(a + w, 0));
    if (demi) arcPoints(Pc(a + w, h / 2), (h / 2) * PXC, -Math.PI / 2, Math.PI / 2).forEach((q) => contour.push(q));
    contour.push(Pc(a + w, h));
    if (a === 0) contour.push(Pc(0, h), Pc(0, 0));
    else contour.push(Pc(0, h));
    const pieces = [];
    const lignes = [];
    if (a > 0) {
        pieces.push({ nature: 'triangle rectangle', dims: `de côtés ${fr(a)} cm et ${fr(h)} cm`, aire: (a * h) / 2 });
        lignes.push({ p1: Pc(a, 0), p2: Pc(a, h) });
    }
    pieces.push({ nature: 'rectangle', dims: `de ${fr(w)} cm sur ${fr(h)} cm`, aire: w * h });
    if (demi) {
        pieces.push({ nature: 'demi-disque', dims: `de rayon ${fr(h / 2)} cm`, aire: (Math.PI * (h / 2) ** 2) / 2 });
        lignes.push({ p1: Pc(a + w, 0), p2: Pc(a + w, h) });
    }
    return {
        contour,
        lignes,
        pieces,
        operation: 'addition',
        codages: [{ v: Pc(a, h), p: Pc(a + w, h), q: Pc(a, 0) }],
        cotes: (deduire) => {
            const c = [{ p: Pc(-0.9, h / 2), texte: `${fr(h)} cm` }];
            if (deduire) {
                // Les deux cotes sont forcement sous la figure — « a cm » mesure une
                // portion de la base. On les etage donc : la mesure partielle contre la
                // base, le total en dessous (retour prof 2026-09-27).
                if (a > 0) c.push({ p: Pc(a / 2, h + 0.5), texte: `${fr(a)} cm` });
                c.push({ p: Pc((a + w) / 2, h + 1.9), texte: `longueur totale ${fr(a + w)} cm` });
            } else {
                if (a > 0) c.push({ p: Pc(a / 2, h + 0.5), texte: `${fr(a)} cm` });
                c.push({ p: Pc(a + w / 2, h + 0.5), texte: `${fr(w)} cm` });
            }
            return c;
        },
    };
};

/** Tour : un rectangle surmonté d'un parallélogramme (trait de découpage horizontal). */
const tour = ({ w, hr, hp, s }) => ({
    contour: [Pc(0, hp + hr), Pc(w, hp + hr), Pc(w, hp), Pc(w + s, 0), Pc(s, 0), Pc(0, hp)],
    lignes: [{ p1: Pc(0, hp), p2: Pc(w, hp) }],
    // La HAUTEUR du parallelogramme, TRACEE. Sans elle, le « 5 cm » flottait a cote
    // du cote oblique et s'y rattachait — or le cote oblique est plus long que la
    // hauteur (retour prof 2026-09-27). Le segment part du sommet bas-gauche du
    // parallelogramme et descend jusqu'a la ligne de decoupage.
    hauteurs: [{ p1: Pc(s, 0), p2: Pc(s, hp) }],
    pieces: [
        { nature: 'rectangle', dims: `de ${fr(w)} cm sur ${fr(hr)} cm`, aire: w * hr },
        { nature: 'parallélogramme', dims: `de base ${fr(w)} cm et de hauteur ${fr(hp)} cm`, aire: w * hp },
    ],
    operation: 'addition',
    codages: [{ v: Pc(0, hp + hr), p: Pc(w, hp + hr), q: Pc(0, hp) }],
    cotes: (deduire) => {
        const c = [
            { p: Pc(w / 2, hp + hr + 0.55), texte: `${fr(w)} cm` },
            // La hauteur du rectangle passe a DROITE : son cote droit mesure lui aussi
            // hr, et la gauche est prise par « hauteur totale », ecrite a la verticale.
            // Les deux se chevauchaient (retour prof 2026-09-27).
            { p: Pc(w + 0.9, hp + hr / 2), texte: `${fr(hr)} cm` },
        ];
        c.push(deduire
            ? { p: Pc(-0.6, (hp + hr) / 2), texte: `hauteur totale ${fr(hp + hr)} cm`, vertical: true }
            // contre le segment de hauteur, pas dans le vide a gauche de la figure
            : { p: Pc(s - 0.55, hp / 2), texte: `${fr(hp)} cm`, interne: true });
        return c;
    },
});

/** Coin : un rectangle dont on RETIRE un triangle rectangle au coin haut-droit. */
const coin = ({ L, l, t, u }) => ({
    contour: [Pc(0, 0), Pc(L - t, 0), Pc(L, u), Pc(L, l), Pc(0, l)],
    lignes: [{ p1: Pc(L - t, 0), p2: Pc(L, 0) }, { p1: Pc(L, 0), p2: Pc(L, u) }],
    // Le triangle retire, dessine comme une piece plutot que par ses deux cotes.
    retrait: [Pc(L - t, 0), Pc(L, 0), Pc(L, u)],
    pieces: [
        { nature: 'rectangle', dims: `de ${fr(L)} cm sur ${fr(l)} cm`, aire: L * l },
        { nature: 'triangle rectangle', dims: `de côtés ${fr(t)} cm et ${fr(u)} cm`, aire: (t * u) / 2 },
    ],
    operation: 'soustraction',
    codages: [{ v: Pc(0, 0), p: Pc(L - t, 0), q: Pc(0, l) }],
    cotes: () => [
        { p: Pc(L / 2, l + 0.55), texte: `${fr(L)} cm` },
        // La hauteur du rectangle passe a GAUCHE (retour prof 2026-09-27). A droite
        // elle etait posee entre y = 0 et y = u, c'est-a-dire le long du VIDE laisse
        // par le triangle retire : il n'y a aucun segment de l cm a cet endroit. Le
        // cote gauche, lui, mesure exactement l, et rien n'y est pose.
        { p: Pc(-0.95, l / 2), texte: `${fr(l)} cm` },
        { p: Pc(L - t / 2, -0.6), texte: `${fr(t)} cm` },
        { p: Pc(L + 0.95, u / 2), texte: `${fr(u)} cm` },
    ],
});

/** Encoche : un rectangle dont on RETIRE un demi-disque creusé dans le bord droit. */
const encoche = ({ L, l, r }) => {
    const contour = [Pc(0, 0), Pc(L, 0), Pc(L, l / 2 - r)];
    arcPoints(Pc(L, l / 2), r * PXC, -Math.PI / 2, Math.PI / 2).reverse()
        .forEach((q) => contour.push({ x: 2 * L * PXC - q.x, y: q.y }));
    contour.push(Pc(L, l / 2 + r), Pc(L, l), Pc(0, l));
    return {
        contour,
        // `lignes` reste la DONNEE du decoupage — la spec borne leur nombre a [1;3] —
        // mais ce n'est plus elle qu'on trace : voir `retrait`.
        lignes: [{ p1: Pc(L, l / 2 - r), p2: Pc(L, l / 2 + r) }],
        // Le demi-disque retire, decrit comme un polygone : sa corde est le bord
        // droit du carre, son arc est celui qu'on voit creuse dans la figure.
        retrait: [
            Pc(L, l / 2 - r),
            ...arcPoints(Pc(L, l / 2), r * PXC, -Math.PI / 2, Math.PI / 2)
                .reverse()
                .map((q) => ({ x: 2 * L * PXC - q.x, y: q.y })),
            Pc(L, l / 2 + r),
        ],
        pieces: [
            { nature: 'rectangle', dims: `de ${fr(L)} cm sur ${fr(l)} cm`, aire: L * l },
            { nature: 'demi-disque', dims: `de rayon ${fr(r)} cm`, aire: (Math.PI * r * r) / 2 },
        ],
        operation: 'soustraction',
        codages: [{ v: Pc(0, 0), p: Pc(L, 0), q: Pc(0, l) }],
        cotes: () => [
            { p: Pc(L / 2, l + 0.55), texte: `${fr(L)} cm` },
            { p: Pc(-0.9, l / 2), texte: `${fr(l)} cm` },
            { p: Pc(L - 1.4, l / 2), texte: `rayon ${fr(r)} cm` },
        ],
    };
};

const FAMILLES = { bande, tour, coin, encoche };

/** Assemble une figure complexe complète, avec son aire EXACTE (jamais arrondie avant la somme). */
const construireFigure = ({ famille, params, deduire = 0, afficherLignes = true, seed = '0' }) => {
    const f = FAMILLES[famille](params);
    const cotes = f.cotes(deduire > 0).filter((c) => c.texte);
    const aire = f.operation === 'addition'
        ? f.pieces.reduce((s, p) => s + p.aire, 0)
        : f.pieces[0].aire - f.pieces.slice(1).reduce((s, p) => s + p.aire, 0);
    return {
        famille,
        pieces: f.pieces,
        operation: f.operation,
        nombre_de_pieces_elementaires: f.pieces.length,
        nombre_de_lignes_de_decoupage: f.lignes.length,
        nombre_de_dimensions_a_deduire: deduire,
        longueurs_cotees: cotes.length,
        aire,
        avecDisque: f.pieces.some((p) => /disque/.test(p.nature)),
        svg: rendreCplx({
            contour: f.contour, lignes: f.lignes, cotes, codages: f.codages, seed, afficherLignes,
            retrait: f.retrait, hauteurs: f.hauteurs,
        }),
    };
};

/** Chaque pièce pèse au moins un dixième de l'aire totale (exclusion 4). */
const piecesEquilibrees = (fig) => {
    const total = fig.operation === 'addition'
        ? fig.pieces.reduce((s, p) => s + p.aire, 0)
        : fig.pieces[0].aire;
    return fig.pieces.every((p) => p.aire >= total / 10);
};

const dim = () => randInt(DIM_MIN * 2, DIM_MAX * 2) / 2;   // décimal à une décimale (demi-entier)

/** Tirages de figures, par famille, tous bornés par `longueur_d_une_dimension_cotee`. */
const tirerFigure = (familles, { deduire = 0, afficherLignes = true } = {}) => {
    for (let i = 0; i < 300; i++) {
        const famille = randomChoice(familles);
        let params;
        if (famille === 'bande') {
            const h = randInt(4, 10);
            params = { a: randInt(3, 7), w: randInt(4, 9), h, demi: false };
        } else if (famille === 'tour') {
            params = { w: randInt(4, 10), hr: randInt(3, 7), hp: randInt(3, 6), s: randInt(2, 4) };
        } else if (famille === 'coin') {
            const L = randInt(7, 12);
            const l = randInt(5, 9);
            params = { L, l, t: randInt(3, L - 3), u: randInt(2, l - 2) };
        } else {
            const l = 2 * randInt(3, 5);
            params = { L: randInt(7, 12), l, r: l / 2 };
        }
        const fig = construireFigure({ famille, params, deduire, afficherLignes, seed: salt(randInt(0, 99999)) });
        if (!piecesEquilibrees(fig)) continue;
        if (fig.pieces.some((p) => p.aire <= 0)) continue;
        return { ...fig, params };
    }
    const params = { w: 8, hr: 5, hp: 4, s: 3 };
    return { ...construireFigure({ famille: 'tour', params, deduire, afficherLignes, seed: 'repli' }), params };
};

/** Bande à TROIS pièces : triangle rectangle, rectangle, demi-disque. */
const tirerFigureTrois = () => {
    for (let i = 0; i < 300; i++) {
        const h = 2 * randInt(2, 5);
        const params = { a: randInt(3, 7), w: randInt(4, 9), h, demi: true };
        const fig = construireFigure({ famille: 'bande', params, seed: salt(randInt(0, 99999)) });
        if (piecesEquilibrees(fig)) return { ...fig, params };
    }
    const params = { a: 4, w: 6, h: 6, demi: true };
    return { ...construireFigure({ famille: 'bande', params, seed: 'repli3' }), params };
};

const imgEnonce = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'small' } }],
});

const listePieces = (fig) => fig.pieces.map((p) => `un ${p.nature} ${p.dims}`).join(', ');

const enonceComplexe = (fig, intro, avecDims = true) => `<p>${intro}</p>`
    + `<p>Les traits en pointillés montrent la décomposition : ${avecDims ? listePieces(fig) : fig.pieces.map((p) => `un ${p.nature}`).join(', ')}. `
    + `${fig.operation === 'addition'
        ? 'L\'aire de la figure est la SOMME des aires des pièces.'
        : 'La figure est obtenue en RETIRANT la seconde pièce de la première : son aire est une DIFFÉRENCE.'}`
    + `${RAPPEL_UNITE}</p>`;

const paramsComplexe = (fig, niveau, extra) => ({
    niveau,
    famille: fig.famille,
    pieces: fig.pieces.map((p) => p.nature),
    operation: fig.operation,
    nombre_de_pieces_elementaires: fig.nombre_de_pieces_elementaires,
    nombre_de_lignes_de_decoupage: fig.nombre_de_lignes_de_decoupage,
    nombre_de_dimensions_a_deduire: fig.nombre_de_dimensions_a_deduire,
    dimensions: Object.values(fig.params),
    aire: Math.round(fig.aire * 1000) / 1000,
    ...extra,
});

const genComplexeAccolees = () => {
    const fig = tirerFigure(['bande', 'tour']);
    return meta(saisieAire(fig.aire, enonceComplexe(fig,
        'Cette figure est obtenue en accolant deux pièces. Calcule son aire.'), L_ACCOLEES, {
        elements_additionnels: imgEnonce(fig.svg),
    }), 'complexe_accolees_texte', paramsComplexe(fig, 3, { erreursVisees: [E_UNITE] }));
};

const genComplexeDetaille = () => {
    const fig = tirerFigure(['bande', 'tour']);
    // Un trou par aire partielle, puis un trou pour le total : le barème est
    // additif, chaque trou étant noté séparément (`type_reponse`).
    const phrase = fig.pieces.map((p, i) => `Aire de la pièce ${i + 1} (${p.nature}) = {{${fr(p.aire)}}} cm².`).join(' ')
        + ` Aire totale = {{${fr(fig.aire)}}} cm².`;
    const texte = enonceComplexe(fig,
        'Cette figure est obtenue en accolant deux pièces. Calcule l\'aire de chaque pièce, puis l\'aire totale.');
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_ACCOLEES), mode: 'saisie', strict: false,
        elements_additionnels: imgEnonce(fig.svg),
    });
    return meta(payload, 'complexe_accolees_texte_trou_saisie', paramsComplexe(fig, 3, { detaille: true }));
};

const genComplexeRetrait = () => {
    const fig = tirerFigure(['coin', 'encoche']);
    const arrondi = fig.avecDisque;
    const aire = arrondi ? Math.round((fig.pieces[0].aire - (3.14 * fig.params.r ** 2) / 2) * 10) / 10 : fig.aire;
    const intro = 'Cette figure est obtenue en retirant une pièce d\'une autre. Calcule son aire.'
        + (arrondi ? ' On prendra π ≈ 3,14 et on arrondira le résultat au dixième, à la fin du calcul seulement.' : '');
    return meta(saisieAire(aire, enonceComplexe(fig, intro), L_RETRAIT, {
        elements_additionnels: imgEnonce(fig.svg),
    }), 'complexe_retrait_texte', paramsComplexe(fig, 4, {
        arrondi_annonce: arrondi ? 'dixième, π ≈ 3,14' : 'valeur exacte',
        aire: aire,
        erreursVisees: [E_ADDITION, E_UNITE],
    }));
};

const genComplexeTrois = () => {
    const fig = tirerFigureTrois();
    const aire = Math.round((fig.pieces[0].aire + fig.pieces[1].aire
        + (3.14 * (fig.params.h / 2) ** 2) / 2) * 10) / 10;
    const intro = 'Cette figure est obtenue en accolant trois pièces. Calcule son aire. '
        + 'On prendra π ≈ 3,14 et on arrondira le résultat au dixième, à la fin du calcul seulement.';
    return meta(saisieAire(aire, enonceComplexe(fig, intro), L_TROIS, {
        elements_additionnels: imgEnonce(fig.svg),
    }), 'complexe_trois_texte', paramsComplexe(fig, 5, {
        arrondi_annonce: 'dixième, π ≈ 3,14',
        aire,
        erreursVisees: [E_OUBLIEE, E_UNITE],
    }));
};

const genComplexeDeduire = () => {
    const fig = tirerFigure(['bande', 'tour'], { deduire: 1 });
    const intro = 'Une dimension n\'est pas cotée : elle se retrouve en une seule soustraction à '
        + 'partir des longueurs données. Calcule ensuite l\'aire de la figure.';
    return meta(saisieAire(fig.aire, enonceComplexe(fig, intro, false), L_DEDUIRE, {
        elements_additionnels: imgEnonce(fig.svg),
    }), 'complexe_deduire_texte', paramsComplexe(fig, 6, { erreursVisees: [E_CONTOUR, E_UNITE] }));
};

const genDecomposer = () => {
    const fig = tirerFigure(['bande', 'tour', 'coin'], { afficherLignes: false });
    const vraie = fig.operation === 'addition'
        ? `En ${listePieces(fig)} : on ajoute leurs aires.`
        : `En ${listePieces(fig)} : on retire la seconde aire de la première.`;
    const options = [
        { choix: vraie, code: null },
        {
            choix: fig.operation === 'addition'
                ? `En ${listePieces(fig)} : on retire la seconde aire de la première.`
                : `En ${listePieces(fig)} : on ajoute leurs aires.`,
            code: E_ADDITION,
        },
        { choix: 'En un seul trapèze : on applique la formule de l\'aire du trapèze.', code: E_SANS_FORMULE },
        { choix: `En un seul ${fig.pieces[0].nature} ${fig.pieces[0].dims} : les autres morceaux sont trop petits pour compter.`, code: E_OUBLIEE },
        { choix: 'On additionne les périmètres des pièces : cela donne l\'aire de la figure.', code: E_PERIMETRES },
    ];
    const texte = '<p>Aucun trait de découpage n\'est tracé sur cette figure. Comment la décomposer '
        + 'pour calculer son aire ?</p>'
        + '<p><em>Les pièces choisies doivent être des figures dont on sait calculer l\'aire, et leurs '
        + 'dimensions doivent se lire sur la figure.</em></p>';
    const payload = makeQcm(options.map((o) => o.choix), vraie, texte, {
        ...opts(L_DECOMPOSER), elements_additionnels: imgEnonce(fig.svg),
    });
    return meta(payload, 'decomposer_qcm_unique', paramsComplexe(fig, 7, {
        distracteurs: options.slice(1),
        lignes_affichees: false,
    }));
};

// ═══════════════════════════════════════════════════════════
// Carte 9 — demi_disques (niveau 5) : π laissé dans le résultat
// ═══════════════════════════════════════════════════════════

const VB_DISQ = { w: 360, h: 320 };
const ROUGE_FOND = '#fca5a5';
const ROUGE_TRAIT = '#b91c1c';
const BLEU_FOND = '#93c5fd';
const BLEU_TRAIT = '#1d4ed8';

const cotePQ = (P, Q) => {
    const M = milieu(P, Q);
    const r = dist(P, Q) / 2;
    const dx = (Q.x - P.x) / (2 * r);
    const dy = (Q.y - P.y) / (2 * r);
    return { M, r, nx: -dy, ny: dx };
};

/** Demi-disque de diamètre [PQ] à l'extérieur : arc ÉCHANTILLONNÉ, aucun sweep-flag. */
const demiDisqueExterne = (P, Q, fond, trait) => {
    const { M, r, nx, ny } = cotePQ(P, Q);
    const ux = (P.x - M.x) / r;
    const uy = (P.y - M.y) / r;
    const pts = [];
    for (let i = 0; i <= 24; i++) {
        const t = (Math.PI * i) / 24;
        const co = Math.cos(t);
        const si = Math.sin(t);
        pts.push(`${(M.x + r * (co * ux + si * nx)).toFixed(1)},${(M.y + r * (co * uy + si * ny)).toFixed(1)}`);
    }
    return `<polygon points="${pts.join(' ')}" fill="${fond}" stroke="${trait}" stroke-width="1.6"/>`;
};

const etiquetteHalo = (fig, P, txt, { fs = 13, poids = 400 } = {}) =>
    fig.brut(`<text x="${P.x.toFixed(1)}" y="${P.y.toFixed(1)}" font-family="sans-serif" `
        + `font-size="${fs}" font-weight="${poids}" fill="#0f172a" text-anchor="middle" `
        + `paint-order="stroke" stroke="#ffffff" stroke-width="3.5" stroke-linejoin="round">${txt}</text>`);

const figDemiDisques = (b, c, h, seed) => {
    const px = 17;
    const hx = h * px;
    const cx = c * px;
    const slant = Math.sqrt(Math.max(cx * cx - hx * hx, 1));
    const { A, B, C, D } = paraParVecteurs({ x: 0, y: 0 }, { x: b * px, y: 0 }, { x: slant, y: -hx });
    const sommet = (P, Q) => {
        const { M, r, nx, ny } = cotePQ(P, Q);
        return { x: M.x + nx * r, y: M.y + ny * r };
    };
    const cad = cadreur([A, B, C, D, sommet(A, B), sommet(B, C), sommet(C, D), sommet(D, A)], VB_DISQ, 18);
    const [pA, pB, pC, pD] = [A, B, C, D].map(cad);
    const fig = creerFigure(VB_DISQ, seed);
    fig.brut(demiDisqueExterne(pA, pB, ROUGE_FOND, ROUGE_TRAIT));
    fig.brut(demiDisqueExterne(pB, pC, BLEU_FOND, BLEU_TRAIT));
    fig.brut(demiDisqueExterne(pC, pD, ROUGE_FOND, ROUGE_TRAIT));
    fig.brut(demiDisqueExterne(pD, pA, BLEU_FOND, BLEU_TRAIT));
    fig.brut(`<polygon points="${[pA, pB, pC, pD].map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}" `
        + 'fill="#fde68a" stroke="#0f172a" stroke-width="2"/>');
    const pied = { x: pD.x, y: pA.y };
    segmentPointille(fig, pD, pied);
    codageDroitInterieur(fig, pied, pA, pD, { c: 9 });
    etiquetteHalo(fig, { x: (pD.x + pied.x) / 2 + 20, y: (pD.y + pied.y) / 2 + 4 }, `${h} cm`);
    const mAB = milieu(pA, pB);
    etiquetteHalo(fig, { x: mAB.x + (pD.x - pA.x) / 2, y: mAB.y - 9 }, `${b} cm`);
    const cAD = cotePQ(pD, pA);
    etiquetteHalo(fig, { x: cAD.M.x + cAD.nx * 20, y: cAD.M.y + cAD.ny * 20 + 4 }, `${c} cm`);
    const G = milieu(milieu(pA, pB), milieu(pC, pD));
    [[pA, 'A'], [pB, 'B'], [pC, 'C'], [pD, 'D']].forEach(([P, nom]) => {
        const a = (angleVers(G, P) * Math.PI) / 180;
        fig.brut(`<circle cx="${P.x.toFixed(1)}" cy="${P.y.toFixed(1)}" r="2.6" fill="#0f172a"/>`);
        etiquetteHalo(fig, { x: P.x + Math.cos(a) * 15, y: P.y - Math.sin(a) * 15 + 5 }, nom, { fs: 15, poids: 700 });
    });
    return fig;
};

const genDemiDisques = () => {
    const paires = [[6, 4], [8, 4], [8, 6], [10, 4], [10, 6], [10, 8]];
    let b = 8;
    let c = 6;
    let h = 4;
    for (let essai = 0; essai < 60; essai++) {
        [b, c] = randomChoice(paires);
        // La hauteur relative à [AB] ne peut dépasser le côté [AD], et elle reste
        // dans ses propres bornes (entier de 3 à 9 cm).
        const bas = Math.max(HAUT_MIN, Math.ceil(c / 2));
        const haut = Math.min(HAUT_MAX, c - 1);
        if (bas > haut) continue;
        h = randInt(bas, haut);
        if (b * h !== (b / 2) ** 2 && b * h !== (c / 2) ** 2) break;
    }
    const r1 = b / 2;
    const r2 = c / 2;
    const bh = b * h;
    const aR = r1 * r1;
    const aB = r2 * r2;
    const fig = figDemiDisques(b, c, h, salt(b, c, h, randInt(0, 999)));
    const phrase = `Aire du parallélogramme = {{${bh}}} cm². `
        + `Aire du disque rouge (rayon ${r1} cm) = {{${aR}}}π cm². `
        + `Aire du disque bleu (rayon ${r2} cm) = {{${aB}}}π cm². `
        + `Aire totale coloriée = ({{${bh}}} + {{${aR + aB}}}π) cm².`;
    const texte = `<p>ABCD est un parallélogramme avec AB = ${b} cm, AD = ${c} cm et une hauteur `
        + `relative à [AB] de ${h} cm. Quatre demi-disques sont construits à l'extérieur : deux rouges, `
        + 'de diamètres [AB] et [CD], et deux bleus, de diamètres [AD] et [CB]. Les deux demi-disques '
        + 'rouges forment un disque, les deux bleus aussi.</p>'
        + '<p>Calcule l\'aire totale coloriée, pièce par pièce. On laisse π dans le résultat : aucune '
        + 'aire partielle n\'est arrondie.</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DISQUES), mode: 'saisie', strict: false, elements_additionnels: imgEnonce(fig.render()),
    });
    return meta(payload, 'demi_disques_texte_trou_saisie', {
        niveau: 5,
        famille: 'parallelogramme et demi-disques',
        pieces: ['parallélogramme', 'demi-disque', 'demi-disque', 'demi-disque'],
        operation: 'addition',
        nombre_de_pieces_elementaires: 3,
        longueur_de_la_base_du_parallelogramme: b,
        longueur_de_la_hauteur_du_parallelogramme: h,
        second_cote: c,
        arrondi_annonce: 'π laissé dans le résultat',
        detaille: true,
        erreursVisees: [E_OUBLIEE],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 10 — formule (transverse)
// ═══════════════════════════════════════════════════════════

const genFormule = () => {
    const { b, h } = tirerParallelogramme();
    const items = [
        'On trace la hauteur relative à la base.',
        'On découpe le triangle rectangle qui dépasse d\'un côté.',
        'On recolle ce triangle de l\'autre côté.',
        `On obtient un rectangle de base ${b} cm et de hauteur ${h} cm.`,
        `L'aire n'a pas changé : elle vaut donc ${b} × ${h} = ${b * h} cm².`,
    ];
    const texte = '<p>On veut montrer que l\'aire d\'un parallélogramme vaut base × hauteur relative à '
        + 'cette base. Remets les étapes de la manipulation dans le bon ordre.</p>';
    const payload = makeOrdonner(items, texte, opts(L_FORMULE));
    return meta(payload, 'formule_ordonner', {
        niveau: 1,
        longueur_de_la_base_du_parallelogramme: b,
        longueur_de_la_hauteur_du_parallelogramme: h,
        aire: b * h,
        erreursVisees: [E_DEUX_COTES],
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    aire_directe_texte: genAireDirecte,
    hauteur_quelconque_texte: genHauteurQuelconque,
    paire_qcm_unique: genPaire,
    complexe_accolees_texte: genComplexeAccolees,
    complexe_accolees_texte_trou_saisie: genComplexeDetaille,
    complexe_retrait_texte: genComplexeRetrait,
    complexe_trois_texte: genComplexeTrois,
    complexe_deduire_texte: genComplexeDeduire,
    decomposer_qcm_unique: genDecomposer,
    demi_disques_texte_trou_saisie: genDemiDisques,
    formule_ordonner: genFormule,
};

const FORMAT_LABELS = {
    texte: 'Saisie', texte_trou_saisie: 'Saisie détaillée', qcm_unique: 'QCM', ordonner: 'Ordonner',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'aire_directe', label: L_DIRECTE, formatsList: buildFormats('aire_directe') },
    { id: 'hauteur_quelconque', label: L_QUELCONQUE, formatsList: buildFormats('hauteur_quelconque') },
    { id: 'paire', label: L_PAIRE, formatsList: buildFormats('paire') },
    { id: 'complexe_accolees', label: L_ACCOLEES, formatsList: buildFormats('complexe_accolees') },
    { id: 'complexe_retrait', label: L_RETRAIT, formatsList: buildFormats('complexe_retrait') },
    { id: 'complexe_trois', label: L_TROIS, formatsList: buildFormats('complexe_trois') },
    { id: 'complexe_deduire', label: L_DEDUIRE, formatsList: buildFormats('complexe_deduire') },
    { id: 'decomposer', label: L_DECOMPOSER, formatsList: buildFormats('decomposer') },
    { id: 'demi_disques', label: L_DISQUES, formatsList: buildFormats('demi_disques') },
    { id: 'formule', label: L_FORMULE, formatsList: buildFormats('formule') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    tirerParallelogramme, tirerFigure, tirerFigureTrois, construireFigure,
    piecesEquilibrees, FAMILLES, ecrituresAire, dim, shuffle,
};
